import type { FormEventHandler } from 'react'
import { Fragment, useState } from 'react'

import SaveIcon from '@mui/icons-material/Save'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Switch from '@mui/material/Switch'
import { DatePicker } from '@mui/x-date-pickers'

import type { DisciplineVerzameling } from '@models/DisciplineVerzameling'

import { useSaveEvent } from '../../hooks/useSaveEvent'
import type { HoofdleidingProject } from '../../types/HoofdleidingProject'

const dateFormat = 'dd-MM-yyyy'

type Props = {
  project: HoofdleidingProject
  handleClose: () => void
}

export const TechnischGereed = ({ project, handleClose }: Props) => {
  const { mutate: saveEvent } = useSaveEvent()

  const [disciplines, setDisciplines] = useState(
    project.disciplineVerzamelingen,
  )

  const handleDisciplineUpdate = ({
    disciplineCode,
    netbeheerderCode,
    technischGereed,
  }: DisciplineVerzameling) => {
    setDisciplines((disciplines) =>
      disciplines.map((d) => {
        if (
          d.disciplineCode === disciplineCode &&
          d.netbeheerderCode === netbeheerderCode
        ) {
          return {
            ...d,
            technischGereed,
          }
        }

        return d
      }),
    )
  }

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()

    saveEvent(
      {
        id: project.aanvraagID,
        type: 'technisch_gereed',
        source: 'manual',
        data: {
          disciplineVerzamelingen: disciplines,
        },
      },
      { onSuccess: () => handleClose() },
    )
  }

  return (
    <form onSubmit={onSubmit}>
      <DialogTitle>Technisch gereed melden</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ marginTop: 2 }}>
          {disciplines.map((discipline) => (
            <Fragment
              key={discipline.disciplineCode + discipline.netbeheerderCode}
            >
              <Grid size={{ xs: 5 }}>
                <FormControlLabel
                  label={`${discipline.disciplineLabel} - ${discipline.netbeheerderLabel}`}
                  control={
                    <Switch
                      checked={Boolean(discipline.technischGereed)}
                      onChange={(_, checked) => {
                        handleDisciplineUpdate({
                          ...discipline,
                          technischGereed: checked ? new Date() : undefined,
                        })
                      }}
                    />
                  }
                />
              </Grid>

              <Grid size={{ xs: 7 }} sx={{ minHeight: '56px' }}>
                {discipline.technischGereed && (
                  <DatePicker
                    format={dateFormat}
                    label="Technisch gereed datum"
                    value={new Date(discipline.technischGereed)}
                    onChange={(value) => {
                      if (value) {
                        handleDisciplineUpdate({
                          ...discipline,
                          technischGereed: new Date(value),
                        })
                      }
                    }}
                    displayWeekNumber
                  />
                )}
              </Grid>
            </Fragment>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} autoFocus>
          Annuleren
        </Button>

        <Button type="submit" variant="contained" startIcon={<SaveIcon />}>
          Opslaan
        </Button>
      </DialogActions>
    </form>
  )
}
